import { IBidResponse } from '../../typings/IPrebid'
import { IBidRequest } from '../../typings/IPrebidAdapter'
import { Event } from '../eventManager/EventManager'
import WebModel from '../models/WebModel'
import {
  parseBidRequest,
  parseBidResponse,
  parseBidTimeout,
  parseGoogletagEvent,
  getBaseData
} from './BidParsers'
import { IMetricsClient } from './MetricsTracker'
import { newRelicAdapter } from './newRelic/NewRelicAdapter'
import IVideoConfig from '../../typings/IVideoConfig'
import { Device } from '../helpers'
import { Flooring } from '../flooring/Flooring'
import { Slot } from '../slots/Slot'
import { IReportingObserverReport } from '../eventManager/helpers/globalRegistration'
import { Metric } from 'web-vitals'
import StickyVideo from '../stickyVideo/StickyVideo'

export class MetricsNewRelicClient implements IMetricsClient {
  constructor(readonly model: WebModel, readonly newrelic = newRelicAdapter) {
    if (!newrelic) {
      return
    }
    // Disables NewRelic error handling so Sentry can do its job better.
    // newrelic.setErrorHandler((err) => true)

    newrelic.setCustomAttribute('windowHeight', window.innerHeight)
    newrelic.setCustomAttribute('windowWidth', window.innerWidth)
  }

  record(event: Event.bidTimeout, data: Array<IBidRequest>): void
  record(event: Event.bidRequested, data: IBidRequest): void
  record(event: Event.bidResponse, data: IBidResponse): void
  record(event: Event.initLauncher, data: IVideoConfig): void
  record(
    event: Event.outstreamAdError,
    data: { adError: google.ima.AdError; bidResponse: IBidResponse }
  ): void
  record(event: Event.heavyAdRemoved, data: IReportingObserverReport): void
  record(event: Event.coreVital, data: Metric): void
  // these typings aren't being inflected by the switch statement for some reason =(
  record(event: Event, data: any, newrelic = newRelicAdapter): void {
    if (!newrelic) {
      return
    }

    switch (event) {
      case Event.initLauncher:
        newrelic.addPageAction(Event.initLauncher, getBaseVideoData(data))
        break
      case Event.bidResponse:
        newrelic.addPageAction(
          'bidResponse',
          parseBidResponse(data, this.model)
        )
        break
      case Event.bidWon:
        newrelic.addPageAction('bidWon', parseBidResponse(data, this.model))
        break
      case Event.bidRequested:
        if (this.model.newRelicBidRequests) {
          newrelic.addPageAction(
            'bidRequested',
            parseBidRequest(data, this.model)
          )
        }
        break
      case Event.bidTimeout:
        data.forEach((bid) => {
          newrelic.addPageAction('bidTimeout', parseBidTimeout(bid, this.model))
        })
        break
      case Event.impressionViewable:
        const slot: Slot = data
        newrelic.addPageAction(Event.impressionViewable, {
          ...getBaseData(this.model),
          adunit: slot.adUnitId
        })
        break
      case Event.slotRenderEnded:
        newrelic.addPageAction(Event.slotRenderEnded, {
          ...parseGoogletagEvent(data, this.model),
          isEmpty: data.isEmpty
        })
        break
      case Event.newRelicLoaded:
        newrelic.addPageAction(Event.newRelicLoaded, data)
        break
      case Event.timeOnSite:
        newrelic.addPageAction(Event.timeOnSite, data)
        break
      case Event.outstreamAdError:
        // get typed data.
        const adError: google.ima.AdError = data.adError
        const bidResponse: IBidResponse = data.bidResponse
        const sendData = {
          ...parseBidResponse(bidResponse, this.model),
          errorCode: adError.getErrorCode(),
          vastErrorCode: adError.getVastErrorCode(),
          type: adError.getType(),
          name: adError.name,
          message: adError.getMessage(),
          innerError: adError.getInnerError(),
          bidFloor: Flooring.getOutstreamFloor(this.model)
        }
        newrelic.addPageAction(Event.outstreamAdError, sendData)
        break
      case Event.heavyAdRemoved:
        newrelic.addPageAction(Event.heavyAdRemoved, data)
        break
      case Event.coreVital:
        const vitalsData = {
          vitalType: data.name,
          group: StickyVideo.group?.version,
          version: StickyVideo.group?.name,
          wrapperGroup: this.model.versionGroup.name,
          wrapperVersion: this.model.versionGroup.version,
          ...data
        }
        newrelic.addPageAction(Event.coreVital, vitalsData)
        break
      default:
    }
  }
}

function getBaseVideoData(config: IVideoConfig): object {
  return {
    deviceType: Device.deviceType,
    domain: window.location.hostname.replace('www.', ''),
    generator: 'video',
    group: config.playerGroup,
    version: config.playerVersion,
    wrapperGroup: window.$mediavine.web.model.versionGroup.name,
    wrapperVersion: window.$mediavine.web.model.versionGroup.version
  }
}
